import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LogOut, Languages, Radio, Shield } from "lucide-react";
import { APP_NAV } from "./navConfig";
import { BrandLogo } from "./Brand";
import { useI18n } from "@/i18n";
import { useSession } from "@/context/SessionContext";
import { useLaunch } from "@/context/LaunchContext";
import { MembershipBadge } from "@/components/badges/Badges";

export function Sidebar() {
  const { d, lang, setLang } = useI18n();
  const { user, tier, logout } = useSession();
  const { isAdmin, plan, isTrial } = useLaunch();
  const navigate = useNavigate();

  return (
    <aside
      className="fixed inset-y-0 left-0 hidden w-[264px] flex-col border-r border-line bg-navy-900/80 backdrop-blur-xl lg:flex"
      data-testid="app-sidebar"
    >
      <div className="flex h-16 items-center border-b border-line px-5">
        <BrandLogo to="/app" />
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
        {APP_NAV.map((item) => (
          <NavLink
            key={item.key}
            to={item.to}
            end={item.to === "/app"}
            data-testid={`nav-${item.key}`}
            className={({ isActive }) =>
              `group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all ${
                isActive
                  ? "bg-atlas/10 text-atlas-300 shadow-[inset_0_0_0_1px_rgba(37,211,102,0.2)]"
                  : "text-muted-foreground hover:bg-white/5 hover:text-foreground"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <item.icon className={`h-[18px] w-[18px] ${isActive ? "text-atlas-400" : ""}`} />
                <span className="flex-1">{item.label(d)}</span>
                {item.key === "live" && <span className="h-1.5 w-1.5 rounded-full bg-live animate-live-pulse" />}
              </>
            )}
          </NavLink>
        ))}
        {isAdmin && (
          <NavLink to="/app/admin" data-testid="nav-admin"
            className={({ isActive }) => `group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all ${isActive ? "bg-gold/10 text-gold-300 shadow-[inset_0_0_0_1px_rgba(233,195,99,0.2)]" : "text-muted-foreground hover:bg-white/5 hover:text-foreground"}`}>
            <Shield className="h-[18px] w-[18px]" />
            <span className="flex-1">{d.nav.admin}</span>
          </NavLink>
        )}
      </nav>

      <div className="border-t border-line p-3">
        <div className="mb-3 flex items-center justify-between rounded-xl bg-white/[0.03] px-3 py-2.5">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-foreground">{user?.displayName || "Genel görünüm"}</p>
            <p className="truncate text-[11px] text-muted-foreground">{user?.email || (lang === "tr" ? "Önizleme modu" : "Preview mode")}</p>
          </div>
          {(plan || tier) ? (
            <span className="flex shrink-0 items-center gap-1" data-testid="sidebar-plan">
              <MembershipBadge tier={(plan ?? tier) as any} />
              {isTrial && <span className="rounded-full border border-gold/25 bg-gold/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-gold-300">{lang === "tr" ? "Deneme" : "Trial"}</span>}
            </span>
          ) : <span className="shrink-0 rounded-full border border-white/10 px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">{lang === "tr" ? "Giriş gerekli" : "Log in"}</span>}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setLang(lang === "tr" ? "en" : "tr")}
            data-testid="lang-toggle"
            className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs font-semibold text-muted-foreground transition hover:text-foreground"
          >
            <Languages className="h-3.5 w-3.5" /> {lang.toUpperCase()}
          </button>
          <button
            onClick={() => { navigate("/"); logout(); }}
            data-testid="logout-button"
            className="flex items-center justify-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs font-semibold text-muted-foreground transition hover:border-live/30 hover:text-live-400"
          >
            <LogOut className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </aside>
  );
}

export function MobileNav() {
  const { d } = useI18n();
  const primary = APP_NAV.filter((n) => n.primary);
  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-navy-900/90 backdrop-blur-xl lg:hidden"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      data-testid="mobile-nav"
    >
      <div className="mx-auto grid max-w-xl grid-cols-5">
        {primary.map((item) => (
          <NavLink
            key={item.key}
            to={item.to}
            end={item.to === "/app"}
            data-testid={`mobilenav-${item.key}`}
            className={({ isActive }) =>
              `relative flex flex-col items-center gap-1 py-2.5 text-[10px] font-medium transition ${
                isActive ? "text-atlas-300" : "text-muted-foreground"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <span className="relative">
                  <item.icon className="h-5 w-5" />
                  {item.key === "live" && <span className="absolute -right-1 -top-0.5 h-1.5 w-1.5 rounded-full bg-live animate-live-pulse" />}
                </span>
                <span>{item.label(d)}</span>
                {isActive && <span className="absolute inset-x-4 top-0 h-0.5 rounded-full bg-atlas-400" />}
              </>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}
